<template>
  <div class="card p-6 mb-2">
    <h6 class="mb-6">Flugdaten</h6>

    <div class="d-flex" style="gap: 16px">
      <div class="widthEditField">
        <b-form-group label="Flugoption">
          <AppSelect
            v-model="editedSeason.flugoption"
            sort="none"
            :options="flugoptionOptions"
            label="Flugoption"
            hide-search
            :highlight="changedFields.includes('flugoption')"
          />
        </b-form-group>
      </div>

      <div class="widthEditField">
        <b-form-group label="SKR Flugangebot">
          <b-input-group size="sm">
            <b-form-input
              size="sm"
              v-model="editedSeason.skrFlugangebot"
              :number="true"
              type="number"
              :class="{ changed: changedFields.includes('skrFlugangebot') }"
              :disabled="isFlugdatenDisabled"
            ></b-form-input>
            <template #append>
              <b-input-group-text>€</b-input-group-text>
            </template>
          </b-input-group>
        </b-form-group>
      </div>
    </div>

    <div class="p-6 mb-2">
      <h6 class="mb-6">Hauptstrecke</h6>
      <div class="d-flex" style="gap: 16px">
        <div class="widthEditField">
          <b-form-group label="Zielland Zielflughäfen">
            <AirportsSucheMultiselect
              :isChangedMetadata="changedFields.includes('ziellandZielflughaefenIataCodes')"
              v-model="editedSeason.ziellandZielflughaefenIataCodes"
              :isMultipleAllowed="true"
              :openDirection="'top'"
              :filterCountry="reise?.reisekuerzel.substring(0, 2)"
              :disabled="isFlugdatenDisabled"
              id="abflughafen"
            ></AirportsSucheMultiselect>
          </b-form-group>
          <b-form-group label="Zielland Zielflughafen Priorität">
            <AppSelect
              v-model="editedSeason.ziellandZielflughafenPrio"
              sort="none"
              :options="editedSeason.ziellandZielflughaefenIataCodes"
              label="Zielland Zielflughafen Priorität"
              :highlight="changedFields.includes('ziellandZielflughafenPrio')"
              :disabled="isFlugdatenDisabled"
            />
          </b-form-group>
        </div>
        <div class="widthEditField">
          <b-form-group label="Zielland Abflughäfen">
            <AirportsSucheMultiselect
              :isChangedMetadata="changedFields.includes('ziellandAbflughaefenIataCodes')"
              v-model="editedSeason.ziellandAbflughaefenIataCodes"
              :isMultipleAllowed="true"
              :openDirection="'top'"
              :filterCountry="reise?.reisekuerzel.substring(0, 2)"
              :disabled="isFlugdatenDisabled"
              id="abflughafen"
            ></AirportsSucheMultiselect>
          </b-form-group>
          <b-form-group label="Zielland Abflughafen Priorität">
            <AppSelect
              v-model="editedSeason.ziellandAbflughafenPrio"
              sort="none"
              :options="editedSeason.ziellandAbflughaefenIataCodes"
              label="Zielland Abflughafen Priorität"
              :highlight="changedFields.includes('ziellandAbflughafenPrio')"
              :disabled="isFlugdatenDisabled"
            />
          </b-form-group>
        </div>
        <div class="widthEditField">
          <b-form-group>
            <template #label>
              <div class="d-flex align-items-center">
                Overnight Outbound
                <b-button
                  class="btn-xs btn py-1 px-2 ml-2 justify-content-center align-items-center d-flex"
                  v-b-tooltip.hover
                  :title="'Der Hinflug erfolgt eventuell über Nacht, Landung im Zielland dann am nächsten oder übernächsten Tag.'"
                >
                  <Info :size="14" class="m-0 p-0" />
                </b-button>
              </div>
            </template>

            <AppSelect
              v-model="editedSeason.overnightOutbound"
              sort="none"
              :options="overnightOptions"
              label="Overnight Outbound"
              placeholder="-"
              hide-search
              :highlight="changedFields.includes('overnightOutbound')"
              :disabled="isFlugdatenDisabled"
            />
          </b-form-group>
          <b-form-group label="Hotelstart vor Landung" class="widthEditField">
            <b-form-checkbox
              size="lg"
              v-model="editedSeason.hotelstartVorLandung"
              :class="{ changed: changedFields.includes('hotelstartVorLandung') }"
              :disabled="isFlugdatenDisabled"
            ></b-form-checkbox>
          </b-form-group>
        </div>
        <div class="widthEditField">
          <b-form-group>
            <template #label>
              <div class="d-flex align-items-center">
                Overnight Inbound
                <b-button
                  class="btn-xs btn py-1 px-2 ml-2 justify-content-center align-items-center d-flex"
                  v-b-tooltip.hover
                  :title="'Der Rückflug erfolgt eventuell über Nacht, Landung in Deutschland dann am nächsten oder übernächsten Tag.'"
                >
                  <Info :size="14" class="m-0 p-0" />
                </b-button>
              </div>
            </template>
            <AppSelect
              v-model="editedSeason.overnightInbound"
              sort="none"
              :options="overnightOptions"
              label="Overnight Inbound"
              placeholder="-"
              hide-search
              class="widthEditField"
              :highlight="changedFields.includes('overnightInbound')"
              :disabled="isFlugdatenDisabled"
            />
          </b-form-group>
          <b-form-group label="Rückflug nach Hotelende" class="widthEditField">
            <b-form-checkbox
              size="lg"
              v-model="editedSeason.rueckflugNachHotelende"
              :class="{ changed: changedFields.includes('rueckflugNachHotelende') }"
              :disabled="isFlugdatenDisabled"
            ></b-form-checkbox>
          </b-form-group>
        </div>
      </div>
    </div>

    <div class="p-6 mb-2 d-flex" style="gap: 16px">
      <div>
        <h6 class="mb-6">Restriktionen Hinflug</h6>
        <div class="d-flex" style="gap: 16px">
          <b-form-group label="Früheste Ankunft" class="widthEditField">
            <DatePicker
              :lang="langConfig"
              type="time"
              v-model="editedSeason.fruehesteAnkunft"
              :show-second="false"
              :format="formatDatetimepicker"
              value-type="format"
              :append-to-body="false"
              :popup-style="{ position: 'absolute', top: '100%', left: '0' }"
              :disabled="isFlugdatenDisabled"
            ></DatePicker>
          </b-form-group>
          <b-form-group label="Späteste Ankunft" class="widthEditField">
            <DatePicker
              :lang="langConfig"
              type="time"
              v-model="editedSeason.spaetesteAnkunft"
              :show-second="false"
              :format="formatDatetimepicker"
              value-type="format"
              :append-to-body="false"
              :popup-style="{ position: 'absolute', top: '100%', left: '0' }"
              :disabled="isFlugdatenDisabled"
            ></DatePicker>
          </b-form-group>
          <b-form-group label="" class="widthCheckboxField d-flex align-items-center">
            <div
              class="d-flex align-items-center p-2"
              :class="{ changed: changedFields.includes('restriktionenHinRollover') }"
            >
              <b-form-checkbox size="lg" v-model="editedSeason.restriktionenHinRollover" :disabled="true" />
              <div>+1</div>
            </div>
          </b-form-group>
        </div>
      </div>
      <div>
        <h6 class="mb-6">Restriktionen Rückflug</h6>
        <div class="d-flex" style="gap: 16px">
          <b-form-group label="Frühester Abflug" class="widthEditField">
            <DatePicker
              :lang="langConfig"
              type="time"
              v-model="editedSeason.fruehesterAbflug"
              :show-second="false"
              :format="formatDatetimepicker"
              value-type="format"
              :append-to-body="false"
              :popup-style="{ position: 'absolute', top: '100%', left: '0' }"
              :disabled="isFlugdatenDisabled"
            >
              <template #input></template>
            </DatePicker>
          </b-form-group>
          <b-form-group label="Spätester Abflug" class="widthEditField">
            <DatePicker
              :lang="langConfig"
              type="time"
              v-model="editedSeason.spaetesterAbflug"
              :show-second="false"
              :format="formatDatetimepicker"
              value-type="format"
              :append-to-body="false"
              :popup-style="{ position: 'absolute', top: '100%', left: '0' }"
              :disabled="isFlugdatenDisabled"
            ></DatePicker>
          </b-form-group>
          <b-form-group label="" class="widthCheckboxField d-flex align-items-center">
            <div
              class="d-flex align-items-center p-2"
              :class="{ changed: changedFields.includes('restriktionenRueckRollover') }"
            >
              <b-form-checkbox size="lg" v-model="editedSeason.restriktionenRueckRollover" :disabled="true" />
              <div>+1</div>
            </div>
          </b-form-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AirportsSucheMultiselect from '@/components/common/airports-suche-multiselect.vue';
import DatePicker from 'vue2-datepicker';
import { langConfig } from '@/core/common/helpers/utils.js';
import AppSelect from '@/components/common/app-select/app-select.vue';
import { Info } from 'lucide-vue';

export default {
  name: 'ReisenMetadataCardFlugdaten',
  components: {
    AirportsSucheMultiselect,
    DatePicker,
    AppSelect,
    Info,
  },
  props: {
    editedSeason: { type: Object, required: true },
    changedFields: { type: Array, default: () => [] },
    reise: { type: Object, default: null },
  },
  data() {
    return {
      langConfig,
      formatDatetimepicker: 'HH:mm',
      flugoptionOptions: [
        { value: 'ReiseMitFlug', text: 'Reise mit Flug' },
        { value: 'ReiseOhneFlug', text: 'Reise ohne Flug' },
        { value: 'ReiseOhneFlugOptionalBuchbar', text: 'Reise ohne Flug - optional buchbar' },
      ],
      overnightOptions: [
        { value: null, text: '-' },
        { value: 0, text: '0' },
        { value: 1, text: '+1' },
        { value: 2, text: '+2' },
      ],
    };
  },
  computed: {
    // Bei einer Reise ohne Flug sind sämtliche Flugdaten-Felder gesperrt
    isFlugdatenDisabled() {
      return this.editedSeason.flugoption === 'ReiseOhneFlug';
    },
  },
  watch: {
    // Beim Wechsel auf "Reise ohne Flug" werden alle flugbezogenen Felder zurückgesetzt
    'editedSeason.flugoption': {
      handler(newValue) {
        if (newValue === 'ReiseOhneFlug') {
          this.editedSeason.skrFlugangebot = null;
          this.editedSeason.ziellandZielflughaefenIataCodes = null;
          this.editedSeason.ziellandZielflughafenPrio = null;
          this.editedSeason.ziellandAbflughaefenIataCodes = null;
          this.editedSeason.ziellandAbflughafenPrio = null;
          this.editedSeason.overnightOutbound = null;
          this.editedSeason.overnightInbound = null;
          this.editedSeason.hotelstartVorLandung = null;
          this.editedSeason.rueckflugNachHotelende = null;
          this.editedSeason.fruehesteAnkunft = null;
          this.editedSeason.spaetesteAnkunft = null;
          this.editedSeason.fruehesterAbflug = null;
          this.editedSeason.spaetesterAbflug = null;
          this.editedSeason.restriktionenHinRollover = null;
          this.editedSeason.restriktionenRueckRollover = null;
        }
      },
    },
    // Die Rollover-Kennzeichen ergeben sich aus dem Vergleich der frühesten/spätesten Zeiten
    editedSeason: {
      handler(v) {
        if (v.fruehesterAbflug != null && v.spaetesterAbflug != null) {
          const compareFruehesterAbflug = new Date().setHours(
            parseInt(v.fruehesterAbflug.substring(0, 2)),
            parseInt(v.fruehesterAbflug.substring(3, 5))
          );
          const compareSpaetesterAbflug = new Date().setHours(
            parseInt(v.spaetesterAbflug.substring(0, 2)),
            parseInt(v.spaetesterAbflug.substring(3, 5))
          );

          if (compareFruehesterAbflug >= compareSpaetesterAbflug) {
            this.editedSeason.restriktionenRueckRollover = true;
          } else {
            this.editedSeason.restriktionenRueckRollover = false;
          }
        } else {
          this.editedSeason.restriktionenRueckRollover = null;
        }

        if (v.fruehesteAnkunft != null && v.spaetesteAnkunft != null) {
          const compareFruehesteAnkunft = new Date().setHours(
            parseInt(v.fruehesteAnkunft.substring(0, 2)),
            parseInt(v.fruehesteAnkunft.substring(3, 5))
          );
          const compareSpaetesteAnkunft = new Date().setHours(
            parseInt(v.spaetesteAnkunft.substring(0, 2)),
            parseInt(v.spaetesteAnkunft.substring(3, 5))
          );

          if (compareFruehesteAnkunft >= compareSpaetesteAnkunft) {
            this.editedSeason.restriktionenHinRollover = true;
          } else {
            this.editedSeason.restriktionenHinRollover = false;
          }
        } else {
          this.editedSeason.restriktionenHinRollover = null;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.widthEditField {
  width: 225px;
}

.widthCheckboxField {
  width: 80px;
}

:deep(.changed) {
  background-color: #3699ff50;
}

:deep(input:disabled) {
  background-color: var(--surface-sunken) !important;
}

:deep(.mx-datepicker-content) {
  margin: 0 !important;
}
</style>
